import { Downloading, Print } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { presentUuid } from '../../helpers/formatters';
import { orderEditPath } from '../../helpers/orders';
import { generatePDF } from './export_pdf';
import OrderDetailsBody from './OrderDetailsBody';

export default function OrderDetailsModal({ open, orderId, onClose, editReturnTo }) {
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [transaction, setTransaction] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [loadingPDF, setLoadingPDF] = useState(false);

    useEffect(() => {
        if (!open || !orderId) {
            return undefined;
        }

        let cancelled = false;
        setIsLoading(true);
        setDidNotLoad(false);
        setOrder(null);
        setTransaction(null);

        api.get(`orders/${orderId}`)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                if (response.data?.success && response.data?.data) {
                    const orderData = response.data.data;
                    setOrder(orderData);
                    return api.get(`orders/${orderId}/transaction`)
                        .then((txResponse) => {
                            if (cancelled) {
                                return;
                            }
                            const tx = txResponse.data?.data?.transaction;
                            if (tx && (tx.id || tx.net_amount != null)) {
                                setTransaction(tx);
                            }
                        })
                        .catch(() => {
                            // Transação é opcional
                        });
                }
                setDidNotLoad(true);
                return undefined;
            })
            .catch(() => {
                if (!cancelled) {
                    setDidNotLoad(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, orderId]);

    const handlePrintReceipt = () => {
        if (!order) {
            return;
        }
        setLoadingPDF(true);
        generatePDF(order);
        setTimeout(() => {
            setLoadingPDF(false);
        }, 1000);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                Detalhes da venda
                {orderId ? ` #${presentUuid(orderId)}` : ''}
            </DialogTitle>
            <DialogContent dividers sx={{ minHeight: 200 }}>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!isLoading && didNotLoad && (
                    <Typography color="error">
                        Não foi possível carregar os detalhes desta venda.
                    </Typography>
                )}
                {!isLoading && !didNotLoad && order && (
                    <OrderDetailsBody
                        order={order}
                        transaction={transaction}
                        onOrderUpdated={setOrder}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Fechar</Button>
                <Button
                    variant="outlined"
                    disabled={!orderId}
                    onClick={() => {
                        onClose?.();
                        navigate(orderEditPath(orderId, editReturnTo));
                    }}
                >
                    Editar pedido
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={loadingPDF ? <Downloading /> : <Print />}
                    onClick={handlePrintReceipt}
                    disabled={loadingPDF || !order}
                >
                    {loadingPDF ? 'Gerando…' : 'Imprimir comprovante'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
